import type { AdminTelemetryDailySeries, AdminTelemetryGroupBy, CampaignStateCount, OperationalMetric } from "../../api/types";
import { formatDateTime } from "../../formatting/date";
import { formatCount, isValidCount } from "../../formatting/number";
import { resolveLifecycleStatus, type StatusTone } from "../../formatting/status";
import { pivotDailySeries } from "./dailySeries";

/**
 * Pure, client-side derivations for `TelemetryScreen` (REDISENO-PANEL.md §6,
 * §9.4 "Únicos valores derivados en el cliente"). No I/O, no React.
 *
 * `metricValue`/`formatMetricDisplay`/`formatDeliveryRateFromMetrics`
 * deliberately duplicate `features/overview/derived.ts`'s own identically-
 * named functions rather than importing them — same precedent T5's
 * `campaignDetailDerived.ts` already established ("kept campaigns/overview
 * independent feature slices, duplicating the two tiny helpers rather than
 * adding a cross-feature dependency").
 */

/** §9.2: finds `key` in a metric catalog and returns its value only when `availability === "available"` — never surfaces a value for a blocked/unavailable metric. */
export function metricValue(metrics: readonly OperationalMetric[], key: string): number | null {
  const found = metrics.find((metric) => metric.key === key);
  if (!found || found.availability !== "available" || !isValidCount(found.value)) return null;
  return found.value;
}

/** §9.2: "si una métrica es unavailable, mostrar '—' y nunca un 0" — a real measured zero still renders as "0". */
export function formatMetricDisplay(metrics: readonly OperationalMetric[], key: string): string {
  const value = metricValue(metrics, key);
  return value === null ? "—" : formatCount(value);
}

const RATE_FORMATTER = new Intl.NumberFormat("es-CO", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** §9.4: "Tasa de entrega = entregado ÷ encolado (1 decimal, '—' si encolado = 0)" — also '—' when either figure is unavailable. */
export function formatDeliveryRateFromMetrics(metrics: readonly OperationalMetric[]): string {
  const encolado = metricValue(metrics, "encolado");
  const entregado = metricValue(metrics, "entregado");
  if (encolado === null || entregado === null || encolado === 0) return "—";
  return `${RATE_FORMATTER.format((entregado / encolado) * 100)} %`;
}

export type TelemetryPeriod = "metrics" | "metricsAllTime";

/**
 * §6 header subtitle: a selected campaign names itself, its scheduled date,
 * and its lifecycle label; otherwise the account-wide scope names the
 * active period.
 *
 * Codex finding 9 (PR #385): previously omitted `fechaProgramada` entirely
 * (just "nombre · estado") -- harmless while the compact `CampaignSelector`
 * could also show the full "nombre · fecha · estado" on its own selected
 * option, but a `?campaignId=` outside that selector's currently-loaded
 * page can't rely on the selector to show it at all, leaving this subtitle
 * as the only place to read the date. Matches the same
 * "nombre · fecha · estado" order `CampaignSelector`'s own option label
 * uses (`Panel Precision App.dc.html`'s prototype format).
 */
export function subtitleFor({
  campaign,
  period = "metrics",
}: {
  campaign: { nombre: string; estado: string; fechaProgramada: string } | null;
  period?: TelemetryPeriod;
}): string {
  if (campaign) {
    return `${campaign.nombre} · ${formatDateTime(campaign.fechaProgramada)} · ${resolveLifecycleStatus(campaign.estado).label}`;
  }
  return period === "metricsAllTime" ? "Toda la cuenta · desde el inicio" : "Toda la cuenta · últimos 7 días";
}

const MONTH_ABBR_LOWER = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/**
 * `yyyy-MM-dd` → "D mmm" lowercase Spanish (e.g. "20 oct"), matching the
 * prototype's own per-bar tooltip format. Deliberately parses the plain
 * string instead of constructing a `Date` — `new Date("2026-01-01")` is
 * midnight UTC, and reading it back with local getters in a negative-offset
 * timezone would read as 31 Dec: this function never goes through a `Date`
 * at all, so there is no UTC/local boundary to cross.
 */
export function formatShortDayMonth(fecha: string): string {
  const [, monthPart = "1", dayPart = "1"] = fecha.split("-");
  const month = Number(monthPart) - 1;
  const day = Number(dayPart);
  // Advisory R3-malformed-fecha: every real caller only ever passes
  // `pivotDailySeries`'s own `yyyy-MM-dd` fechas, so this guard is
  // defensive rather than reachable today -- but without it, a full ISO
  // timestamp (`dayPart` picking up a trailing "T00:00:00Z") or any other
  // non-`yyyy-MM-dd` string produced "NaN oct"/"NaN " instead of a defined
  // fallback.
  if (!Number.isFinite(day) || !Number.isFinite(month) || month < 0 || month > 11) return "—";
  return `${day} ${MONTH_ABBR_LOWER[month] ?? ""}`;
}

export interface DailyChartBar {
  fecha: string;
  entregado: number;
  error: number;
  /** Both counts are a confirmed zero for this day — renders the spec's 3px gray cap instead of two invisible 0-height segments. */
  isZero: boolean;
  deliveredPercent: number;
  errorPercent: number;
  title: string;
}

export interface DailyChartData {
  bars: DailyChartBar[];
  tickLabels: string[];
}

/** §6 "Envíos por día": 4 tick marks at day 1/11/21/30 of the window (indices 0/10/20/last), never one per bar. */
function pickTickIndexes(length: number): number[] {
  if (length === 0) return [];
  const candidates = [0, 10, 20, length - 1];
  const seen = new Set<number>();
  return candidates.filter((index) => {
    if (index < 0 || index >= length || seen.has(index)) return false;
    seen.add(index);
    return true;
  });
}

/**
 * §6 "Envíos por día · últimos 30 días · toda la cuenta" — reuses
 * `pivotDailySeries` (issue #355's own module) so a quiet day with zero
 * events of every type still gets its own bar, never silently disappearing.
 * Bar heights are scaled as a percentage of the busiest day's total
 * (entregado + error), matching the prototype's own `maxDay` denominator.
 */
export function buildDailyChartData(series: AdminTelemetryDailySeries): DailyChartData {
  const pivot = pivotDailySeries(series.points, series.windowStart, series.days);

  const raw = pivot.fechas.map((fecha) => {
    const row = pivot.matrix[fecha] ?? {};
    const entregado = row["entregado"] ?? 0;
    const error = row["error"] ?? 0;
    return { fecha, entregado, error, isZero: entregado === 0 && error === 0 };
  });

  const maxTotal = Math.max(1, ...raw.map((day) => day.entregado + day.error));

  const bars: DailyChartBar[] = raw.map((day) => ({
    ...day,
    deliveredPercent: (day.entregado / maxTotal) * 100,
    errorPercent: (day.error / maxTotal) * 100,
    title: `${formatShortDayMonth(day.fecha)}: ${formatCount(day.entregado)} entregados · ${formatCount(day.error)} errores`,
  }));

  const tickLabels = pickTickIndexes(bars.length).map((index) => formatShortDayMonth(bars[index]!.fecha));

  return { bars, tickLabels };
}

/** §6 "Rendimiento por plantilla | show": a null group name falls back to the fixed "Sin {grupo}" label, never a blank row. */
export function groupLabelFor(name: string | null, groupBy: AdminTelemetryGroupBy): string {
  if (name) return name;
  return groupBy === "show" ? "Sin show" : "Sin plantilla";
}

/** §6 breakdown card meta line: "N enviados · N entregados · N errores", degrading any missing/unavailable figure to "—" instead of a fabricated 0. */
export function formatBreakdownMeta(metrics: readonly OperationalMetric[]): string {
  return `${formatMetricDisplay(metrics, "encolado")} enviados · ${formatMetricDisplay(metrics, "entregado")} entregados · ${formatMetricDisplay(metrics, "error")} errores`;
}

/**
 * Codex finding 5 (PR #385): plural copy for the final disclosure note,
 * local to this note (not `formatting/metrics.ts`'s shared singular
 * `metricLabel`, which other screens use for labels/headers, not prose).
 * Only the F2-01 catalog's 4 keys ever appear here in practice.
 */
const UNMEASURED_METRIC_PLURAL_LABELS: Record<string, string> = {
  apertura: "aperturas",
  clic: "clics",
  rebote_blando: "rebotes blandos",
  rebote_duro: "rebotes duros",
  queja_spam: "quejas de spam",
};

/** Spanish-style list join: "a, b ni c" — matches this note's existing "ni" (not "y") phrasing for a negative disclosure. */
function joinUnmeasuredList(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} ni ${items[items.length - 1]}`;
}

/**
 * §6 final disclosure note: names ONLY the metrics `metrics` actually marks
 * `unavailable` (server-owned F2-01 catalog, `TelemetryCapability.cs`) —
 * never a static list that can drift out of sync when a metric like
 * `rebote_duro` moves from pending to available. Returns `null` (render
 * nothing) once every metric is measured, or while `metrics` isn't loaded
 * yet, instead of fabricating a note.
 */
export function buildUnmeasuredMetricsNote(metrics: readonly OperationalMetric[] | undefined): string | null {
  if (!metrics) return null;
  const unavailable = metrics.filter((metric) => metric.availability === "unavailable");
  if (unavailable.length === 0) return null;
  const labels = unavailable.map((metric) => UNMEASURED_METRIC_PLURAL_LABELS[metric.key] ?? metric.key);
  return `Aún no medimos ${joinUnmeasuredList(labels)}. Aparecerán aquí cuando el sistema empiece a recibirlas.`;
}

export interface CampaignStateLegendEntry {
  state: string;
  label: string;
  tone: StatusTone;
  count: number;
  isZero: boolean;
}

export interface CampaignStateBarSegment {
  state: string;
  label: string;
  tone: StatusTone;
  count: number;
  percent: number;
}

export interface CampaignStateDistribution {
  /** Stacked-bar segments — zero-count states excluded entirely (a 0%-wide segment carries no information). */
  bar: CampaignStateBarSegment[];
  /** Every state from `states`, in the server's own order, including zero counts (2-column legend). */
  legend: CampaignStateLegendEntry[];
}

/**
 * Advisory R3-state-count-unvalidated: unlike `metricValue` (which already
 * guards with `isValidCount`), this function used to sum raw server counts
 * directly -- a negative or non-finite count would produce a NaN total or
 * segment percentages that don't sum to 100. An invalid count is treated as
 * a measured 0 instead, the same graceful-degradation stance the rest of
 * this module already takes.
 */
function safeCount(count: number): number {
  return isValidCount(count) && count >= 0 ? count : 0;
}

/** §6 "Campañas por estado": stacked horizontal bar + 2-column legend, §2 tones, zeros excluded from the bar but kept (grayed) in the legend. */
export function buildCampaignStateDistribution(states: readonly CampaignStateCount[]): CampaignStateDistribution {
  const legend: CampaignStateLegendEntry[] = states.map((entry) => {
    const { label, tone } = resolveLifecycleStatus(entry.state);
    const count = safeCount(entry.count);
    return { state: entry.state, label, tone, count, isZero: count === 0 };
  });

  const total = states.reduce((sum, entry) => sum + safeCount(entry.count), 0);
  const bar: CampaignStateBarSegment[] =
    total === 0
      ? []
      : states
          .filter((entry) => safeCount(entry.count) > 0)
          .map((entry) => {
            const { label, tone } = resolveLifecycleStatus(entry.state);
            const count = safeCount(entry.count);
            return { state: entry.state, label, tone, count, percent: (count / total) * 100 };
          });

  return { bar, legend };
}
